import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Radio (single-select) visual variants — mirror the Figma "Flashka Design
 * System" file `YZK9r3wDhvw1pY60KlVEIj` ("Radio button"). The SELECTED state is
 * specified for each mode by two nodes that INTENTIONALLY resolve to the same
 * rendered colors (verified via `get_variable_defs`):
 *   - 3419:1362 – Large / Selected (light): primary-600 #7f22fe disc ·
 *                 primary-700 #7008e7 border · primary-50 #f5f3ff dot
 *   - 4225:480  – Large / Selected (dark):  primary-400 #7f22fe disc ·
 *                 primary-200 #5d0ec0 border · primary-950 #f5f3ff dot
 * The design uses MIRROR-PAIR shades per mode (600↔400, 50↔950) so the disc
 * (#7f22fe) and dot (#f5f3ff) read IDENTICALLY in light and dark — the selected
 * radio is a brand-purple control, not a surface that should re-tone with the
 * background. Unselected cell: 3419:1374 (secondary-300 ring).
 *
 * Our runtime uses ONE class set over an auto-inverting palette (no `dark:` on
 * native), so any `primary-*` shade would FLIP across modes and break the
 * match — that mirror already bit this component twice (the original
 * `primary-600`/`primary-50` washed out in dark; a "dark-mode tweak" to
 * `primary-400`/`primary-950` turned the dot DARK in light mode). So the
 * selected disc/border/dot are FIXED hex — the same mode-invariant convention
 * the shared Button chips (`Button.variants.ts`, "MODE-INVARIANT in Figma") and
 * Tabs (`Tabs.variants.ts`, `border-stable-primary-600 text-stable-primary-50`) already use.
 * The UNSELECTED ring stays a `secondary-*` token on purpose: it's a neutral
 * outline that SHOULD adapt (light gray on light bg → dark gray on dark bg).
 *
 * Anatomy: a bordered circular `Pressable` that owns size + the (web-only)
 * keyboard focus ring, with a centered inner dot `View` shown only when
 * `checked`. Solid #7f22fe disc + #f5f3ff dot at 50% diameter. No motion spec.
 *
 * Axes:
 *   size:    s (16px) · m (20px, default) · l (24px). Always a full circle.
 *   selected: the checked axis (surfaced as `checked`).
 *
 * Disabled: `opacity-50` — supplied by the `Pressable` primitive on both
 * platforms, so no CVA override is needed.
 */
export const radioRootVariants = cva(
  'relative inline-flex shrink-0 items-center justify-center rounded-full border web:align-middle ' +
    'web:focus-visible:ring-1 web:focus-visible:ring-offset-0 ' +
    'web:transition-colors web:duration-150',
  {
    variants: {
      size: {
        s: 'h-4 w-4',
        m: 'h-5 w-5',
        l: 'h-6 w-6',
      },
      selected: {
        false:
          'border-secondary-300 web:hover:border-secondary-400 web:focus-visible:ring-secondary-600',
        // Fixed hex (mode-invariant brand purple) — see header comment.
        true: 'bg-stable-primary-600 border-stable-primary-700 web:focus-visible:ring-primary-900',
      },
    },
    defaultVariants: { size: 'm', selected: false },
  },
);

/**
 * The inner dot, shown only when selected. 50% of the box diameter (Figma).
 * Fixed `#f5f3ff` (the mode-invariant dot color — Figma primary-50 light ==
 * primary-950 dark), NOT a palette shade, which would flip dark in one mode.
 * See the header comment.
 */
export const radioDotVariants = cva('rounded-full bg-stable-primary-50', {
  variants: {
    size: {
      s: 'h-2 w-2',
      m: 'h-2.5 w-2.5',
      l: 'h-3 w-3',
    },
  },
  defaultVariants: { size: 'm' },
});

export type RadioVariantProps = VariantProps<typeof radioRootVariants>;
